<template>
  <b-container fluid>
    <FormulateForm
      name="updateOrganization"
      @submit="submitHandler"
      #default="{ isLoading }"
      v-model="formValues"
    >
      <b-row>
        <b-col lg="6" offset-lg="3">
          <iq-card>
            <template v-slot:headerTitle>
              <h4 class="card-title">Organization</h4>
            </template>
            <template v-slot:body>
              <b-form-row>
                <b-col>
                  <b-form-group label="Name" label-for="name">
                    <FormulateInput id="name" name="name" type="text" input-class="form-control" />
                  </b-form-group>
                  <b-form-group label="identification number" label-for="in">
                    <FormulateInput id="in" name="in" type="text" input-class="form-control" />
                  </b-form-group>
                  <b-form-group label="Admin" label-for="adminId">
                    <FormulateInput
                      name="adminId"
                      type="select"
                      placeholder="Choose user"
                      :options="
                        users.map((u) => ({
                          value: u.id,
                          label: u.prettyName,
                        }))
                      "
                    />
                  </b-form-group>
                  <b-form-group label="Parent organization" label-for="parentOrganizationId">
                    <FormulateInput
                      name="parentOrganizationId"
                      type="select"
                      placeholder="Choose organization"
                      :validation="parentOrganizationIdComputedRules"
                      :validation-messages="{
                        required: 'Parent organization needs to be defined.'
                      }"
                      :options="
                        parentOrganizations.map((u) => ({
                          value: u.id,
                          label: u.name,
                        }))
                      "
                    />
                  </b-form-group>
                  <b-form-group label="Metadata" label-for="metadata" v-if="isRootOrganization">
                    <FormulateInput
                      name="metadata"
                      type="textarea"
                      validation="isJson|optional"
                      :validation-messages="{
                        isJson: 'The input must be valid JSON.'
                      }"
                    />
                  </b-form-group>
                </b-col>
              </b-form-row>
            </template>
          </iq-card>
        </b-col>
        <b-col lg="2" offset-lg="3">
          <router-link to="/organizations">
            <b-button block variant="outline-primary" class="mb-3 mr-1">Back to organizations</b-button>
          </router-link>
        </b-col>
        <b-col lg="4">
          <b-button
            block
            type="submit"
            variant="primary"
            class="mb-3 mr-1"
            :disabled="isLoading"
            :label="isLoading ? 'Loading...' : 'Submit this form'"
          >Save organization</b-button>
        </b-col>
      </b-row>
    </FormulateForm>
  </b-container>
</template>

<script>
import { core } from "../../config/pluginInit";
import gql from "graphql-tag";
import { prettyName } from '../../Utils/getOwnerData';

const GET_ORGANIZATION = gql`
  query getOrganization($id: uuid!) {
    organizations_by_pk(id: $id) {
      id
      name
      in
      admin_id
      parent_organization_id
      metadata
      organization_id
    }
  }
`;
const LIST_ORGANIZATIONS = gql`
  query listOrganizations {
    organizations {
      id
      name
    }
  }
`;
const LIST_USERS = gql`
  query listUsers {
    users(
      where: {
     		role: {
          _eq: sales_manager
        }   
      }
    ) {
      id
      email
      first_name
      last_name
    }
  }
`;
const UPDATE_ORGANIZATION = gql`
  mutation updateOrganization(
    $id: uuid!,
    $name: String,
    $in: String,
    $adminId: uuid,
    $parentOrganizationId: uuid,
    $metadata: jsonb
  ) {
    update_organizations_by_pk(
      pk_columns: { id: $id }
      _set: {
        name: $name,
        in: $in,
        admin_id: $adminId,
        parent_organization_id: $parentOrganizationId,
        metadata: $metadata
      }
    ) {
      id
      name
      in
      admin_id
      parent_organization_id
      admin {
        id
        email
      }
      metadata
    }
  }
`;

export default {
  name: "OrganizationsEdit",
  data() {
    return {
      formValues: {
        name: "",
        in: null,
        adminId: null,
        parentOrganizationId: null,
        metadata: null,
      },
      users: [],
      organizations: [],
    };
  },
  apollo: {
    organization() {
      return {
        query: GET_ORGANIZATION,
        variables: {
          id: this.$route.params.id
        },
        update: data => {
          this.formValues.name = data.organizations_by_pk.name;
          this.formValues.in = data.organizations_by_pk.in;
          this.formValues.adminId = data.organizations_by_pk.admin_id;
          this.formValues.parentOrganizationId = data.organizations_by_pk.parent_organization_id;
          this.formValues.metadata = data.organizations_by_pk.metadata ? JSON.stringify(data.organizations_by_pk.metadata) : '';
          return data.organizations_by_pk;
        }
      };
    },
    organizations: {
      query: LIST_ORGANIZATIONS
    },
    users() {
      return {
        query: LIST_USERS,
        update: data => data.users.map(i => {
          return {
            ...i,
            prettyName: prettyName(i.first_name, i.last_name, i.email),
          }
        })
      };
    }
  },
  computed: {
    parentOrganizations(){
      return this.organizations.filter(i => i.id !== this.$route.params.id)
    },
    parentOrganizationIdComputedRules(){
      if (this.isRootOrganization) {
        return ''
      }
      return 'required';
    },
    isRootOrganization(){
      if (this.organization.id === this.organization.organization_id && !this.organization.parent_organization_id) {
        return true
      }
      return false;
    }
  },
  methods: {
    async submitHandler() {
      try {
        const vars = {
          id: this.$route.params.id,
          name: this.formValues.name,
          in: this.formValues.in,
          adminId: this.formValues.adminId,
          parentOrganizationId: this.formValues.parentOrganizationId ? this.formValues.parentOrganizationId : undefined,
          metadata: (this.isRootOrganization && this.formValues.metadata) ? JSON.parse(this.formValues.metadata) : undefined,
        };

        await this.$apollo.mutate({
          mutation: UPDATE_ORGANIZATION,
          variables: vars
        });
        this.$toast.success(`Organization updated`);
      } catch (error) {
        this.$toast.error(`Failed to update organization: ${error.message}`);
      }
    }
  },
  mounted() {
    core.index();
  }
};
</script>
